import { Mail, Upload, AlertTriangle, Sparkles, Ban } from "lucide-react";
import { StatusBadge, fmtMoney, fmtDate, AGING_LABEL } from "./apUtils";

function DueCell({ inv }) {
  const overdue = inv.aging && !["current", "no_due_date"].includes(inv.aging);
  return (
    <div>
      <p className={overdue ? "text-rose-700 font-medium" : "text-brand-ink"}>{fmtDate(inv.due_date)}</p>
      {overdue && <p className="text-[10px] uppercase tracking-[0.1em] text-rose-600">{AGING_LABEL[inv.aging]} late</p>}
    </div>
  );
}

function VendorCell({ inv }) {
  const Icon = inv.source === "email" ? Mail : Upload;
  return (
    <div className="flex items-start gap-2 min-w-0">
      <Icon className="h-3.5 w-3.5 text-brand-tertiary mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className="text-brand-ink font-medium truncate">{inv.vendor || "Unknown vendor"}</p>
        <p className="text-xs text-brand-muted truncate">{inv.invoice_number ? `#${inv.invoice_number}` : inv.filename}</p>
      </div>
      {inv.needs_review && <span title={inv.review_reason || ""} data-testid={`ap-row-review-${inv.id}`} className="shrink-0 inline-flex items-center gap-1 text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"><AlertTriangle className="h-3 w-3" /> Needs review</span>}
    </div>
  );
}

export function ApTable({ invoices, onOpen, onForget }) {
  if (!invoices.length) {
    return <div className="border border-dashed border-brand-border rounded-xl py-16 text-center text-sm text-brand-muted" data-testid="ap-empty">No invoices match these filters.</div>;
  }
  return (
    <div className="border border-brand-border rounded-xl overflow-x-auto bg-brand-surface" data-testid="ap-table">
      <table className="w-full text-sm min-w-[880px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-brand-tertiary border-b border-brand-border">
            <th className="px-5 py-3 font-normal">Vendor</th><th className="px-3 py-3 font-normal">Location</th>
            <th className="px-3 py-3 font-normal">Received</th><th className="px-3 py-3 font-normal">Due</th>
            <th className="px-3 py-3 font-normal text-right">Amount</th><th className="px-5 py-3 font-normal">Status</th><th className="px-3 py-3" />
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <tr key={inv.id} onClick={() => onOpen(inv.id)} data-testid={`ap-row-${inv.id}`}
              className="border-b border-brand-border last:border-0 hover:bg-brand-hover cursor-pointer transition-colors duration-150">
              <td className="px-5 py-3 max-w-[280px]"><VendorCell inv={inv} /></td>
              <td className="px-3 py-3 text-brand-muted">
                {inv.location || <span className="text-amber-700">Unassigned</span>}
                {inv.location && inv.location === inv.location_suggested && <Sparkles className="inline h-3 w-3 ml-1 text-brand-tertiary" title="AI-suggested" />}
              </td>
              <td className="px-3 py-3 text-brand-muted">{fmtDate(inv.created_at)}</td>
              <td className="px-3 py-3"><DueCell inv={inv} /></td>
              <td className="px-3 py-3 text-right font-mono text-brand-ink">{fmtMoney(inv.amount)}</td>
              <td className="px-5 py-3"><StatusBadge status={inv.status} testid={`ap-row-status-${inv.id}`} /></td>
              <td className="px-3 py-3 text-right">
                <button onClick={(e) => { e.stopPropagation(); onForget(inv); }} title="Forget this vendor" data-testid={`ap-row-forget-${inv.id}`}
                  className="inline-flex items-center gap-1 whitespace-nowrap text-xs text-brand-muted hover:text-rose-700 transition-colors">
                  <Ban className="h-3 w-3" /> Forget vendor
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
